import { useCallback, useEffect, useState } from 'react';
import {
  createAIAdvisorSession,
  fetchAIAdvisorContext,
  fetchAIAdvisorSession,
  fetchAIRecommendations,
  generateClientAIRecommendations,
  generatePortfolioAIRecommendations,
  postAIAdvisorMessage,
  updateAIRecommendation,
} from '../../services/funding/fundingAIAdvisorService';

export function useFundingAIAdvisor({ agencyId, clientId, autoLoad = true } = {}) {
  const [context, setContext] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const loadContext = useCallback(async () => {
    if (!agencyId) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAIAdvisorContext({ agencyId, clientId });
      setContext(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load advisor context');
      setContext(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [agencyId, clientId]);

  const loadRecommendations = useCallback(async () => {
    if (!agencyId) return [];
    try {
      const result = await fetchAIRecommendations({
        agencyId,
        clientId: clientId || undefined,
        status: 'Open',
      });
      setRecommendations(result.recommendations);
      return result.recommendations;
    } catch (err) {
      setError(err?.message || 'Failed to load recommendations');
      setRecommendations([]);
      return [];
    }
  }, [agencyId, clientId]);

  const ensureSession = useCallback(async () => {
    if (!agencyId) return null;
    if (session?.id) return session;
    const created = await createAIAdvisorSession({
      agencyId,
      clientId: clientId || undefined,
      title: clientId ? 'Client funding strategy' : 'Portfolio advisor session',
    });
    setSession(created);
    setMessages([]);
    return created;
  }, [agencyId, clientId, session]);

  const sendMessage = useCallback(async (content) => {
    if (!agencyId || !content?.trim()) return null;
    setSending(true);
    setError(null);
    try {
      const activeSession = await ensureSession();
      const result = await postAIAdvisorMessage(activeSession.id, {
        agencyId,
        content: content.trim(),
      });
      const bundle = await fetchAIAdvisorSession(activeSession.id, agencyId);
      setSession(bundle.session);
      setMessages(bundle.messages || []);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to send advisor message');
      throw err;
    } finally {
      setSending(false);
    }
  }, [agencyId, ensureSession]);

  const generateRecommendations = useCallback(async () => {
    if (!agencyId) return [];
    setGenerating(true);
    setError(null);
    try {
      if (clientId) {
        const result = await generateClientAIRecommendations(clientId, agencyId);
        setRecommendations((prev) => {
          const ids = new Set(result.recommendations.map((item) => item.id));
          return [...result.recommendations, ...prev.filter((item) => !ids.has(item.id))];
        });
        return result.recommendations;
      }
      const result = await generatePortfolioAIRecommendations(agencyId);
      setRecommendations((prev) => {
        const ids = new Set(result.recommendations.map((item) => item.id));
        return [...result.recommendations, ...prev.filter((item) => !ids.has(item.id))];
      });
      return result.recommendations;
    } catch (err) {
      setError(err?.message || 'Failed to generate recommendations');
      throw err;
    } finally {
      setGenerating(false);
    }
  }, [agencyId, clientId, loadRecommendations, recommendations]);

  const updateRecommendationStatus = useCallback(async (recommendationId, status) => {
    if (!agencyId) return null;
    const updated = await updateAIRecommendation(recommendationId, agencyId, { status });
    setRecommendations((prev) => prev.map((item) => (item.id === recommendationId ? updated : item)));
    return updated;
  }, [agencyId]);

  const refresh = useCallback(async () => {
    await Promise.all([loadContext(), loadRecommendations()]);
  }, [loadContext, loadRecommendations]);

  useEffect(() => {
    if (autoLoad && agencyId) {
      refresh();
    }
  }, [autoLoad, agencyId, clientId, refresh]);

  return {
    context,
    session,
    messages,
    recommendations,
    loading,
    sending,
    generating,
    error,
    loadContext,
    loadRecommendations,
    ensureSession,
    sendMessage,
    generateRecommendations,
    updateRecommendationStatus,
    refresh,
  };
}
